import { ChevronRight } from 'lucide-react';
import { useTranslation } from '../../../../lib/i18n';
import { useItemSeenBi } from '../../../../lib/use-item-seen-bi';
import { ENTITY_TYPE } from '../../../../lib/use-generic-bi';
import { bandConnectUrlFrom, bandOfferSeenFields, bandOptionFields, type BandOfferCardData, type BandPick } from '../../../../lib/band-cards';
import { Card, Typography } from '../../ui';
import { BandAgentRows, BandAppChips } from './BandAgentRows';
import styles from './BandOfferCard.module.css';

/**
 * Two whole teams to choose between, rendered ABOVE an assistant reply carrying
 * `metadata.bandOfferCard` — a sibling of `OpportunityCard` and `PostConnectedConnectorCard`, riding
 * its reply the same way.
 *
 * <h2>Tapping a panel IS the pick</h2>
 *
 * This card used to be inert, on the reasoning that the row's `choices` chips did the choosing. **The
 * harness mints no choices for a band offer** — the agent asks "which band do you want" in prose — so
 * that left two panels describing a decision with no way to take it, and the only way through was to
 * type the band's name. A tap now sends exactly that name as an ordinary user turn (`onPick`), so the
 * card is the affordance and the pick is still expressed one way on the wire.
 *
 * Nothing here is read live either. An offer is a proposal about apps that are not connected yet, so
 * there are no routines to show and no status to keep fresh — the opposite of the band card, whose rows
 * describe connections that have already happened.
 *
 * <h2>…and it has to LOOK like a choice</h2>
 *
 * The panels were buttons from the day `onPick` landed and still read as two paragraphs about the same
 * team: nothing on them said where one option ended, that either was tappable, or what a tap would do.
 * So a pickable panel now carries its own edge, a trailing chevron on the title row, and the ask said in
 * words underneath — the chevron is recognisable once you know the pattern, and this is the first band
 * card an owner ever sees. None of that is a new affordance; it is the missing affordance for the one
 * that already existed.
 */
export function BandOfferCard({
  card,
  conversationId,
  onPick,
}: {
  card: BandOfferCardData;
  /** The turn's conversation, stamped as the impression's entity — the join between "this band was
   *  offered" and everything else that happened in the conversation that offered it. */
  conversationId?: string;
  /** Returns whether the pick was taken. The band's connect walk opens only when it was. */
  onPick?: (option: BandPick) => boolean;
}) {
  const { t } = useTranslation();
  const seenRef = useItemSeenBi('band_offer_card', {
    ...(conversationId ? { entity: { id: conversationId, type: ENTITY_TYPE.conversation } } : {}),
    additionalFields: bandOfferSeenFields(card),
  });

  /**
   * Choosing a band sends the pick AND opens its connect walk, both from this one click.
   *
   * The open has to happen HERE rather than when the band's card comes back, because a browser only lets
   * a script open a window from inside a real gesture — and the chain's finish page closes the tab when it
   * is done, which only a script-opened window is allowed to do. `noopener` is deliberately absent for the
   * same reason it is on the band card's "Connect all": it would sever the relationship that close needs,
   * and the page being opened is our own.
   *
   * **The pick goes first, and the walk opens only if it was taken.** The parent refuses a pick while a
   * turn is streaming and on a historical thread, and opening ahead of that check left a reader
   * authorizing apps for a band nobody had chosen, with nothing in the thread to show they chose it. The
   * send is synchronous, so this is all still one gesture.
   *
   * An option with no link just picks the band, and its created card carries the "Connect all" instead.
   */
  const pick = (option: BandOfferCardData['options'][number]) => {
    if (!onPick?.(bandOptionFields(option))) {
      return;
    }
    if (option.connectUrl) {
      window.open(bandConnectUrlFrom(option.connectUrl, 'web'), '_blank');
    }
  };

  return (
    <Card
      component="section"
      ref={seenRef}
      className={styles.card}
      aria-label={t('band.offerCard.accessibilityLabel')}
      data-testid="chat-band-offer-card"
    >
      {/* Side by side while the bubble has room for both, stacked below that. The threshold is the
          panel's own minimum rather than a viewport query: this card lives in a chat that is a ~370px
          dock, a floating panel OR a full-screen column, so the pane's width is the only honest input. */}
      <div className={styles.options}>
        {card.options.map((option) => (
          // A surface nested in a surface, so it takes the DS's recessed skin rather than a fill of its
          // own. A real button when a pick is possible, an inert `article` on a historical or locked
          // thread — a panel that lights up under the pointer has to be one that answers.
          //
          // The EDGE is the skin's own reserved 1px border (every Card skin carries one at rest,
          // transparent, so a state that draws one cannot shift the layout) coloured in — native's
          // `outlineDefault` stroke over the same fill. Without a fill AND an edge the two bands are two
          // blocks of text on the card's single field, so nothing says where one option ends.
          //
          // No `aria-label`: the button wraps the band's name, tagline, team and apps, so its own content
          // is its accessible name. A terse label would REPLACE all of that with two words.
          <Card
            key={option.id}
            component={onPick ? 'button' : 'article'}
            variant="stone"
            size="sm"
            interactive={onPick ? true : undefined}
            className={styles.option}
            data-band={option.id}
            {...(onPick ? { onClick: () => pick(option) } : {})}
          >
            {/* The chevron is the whole point of this row: it is what says the panel is a choice and not
                a section heading. `dir-flip` mirrors it in RTL, the same as every other chevron here. */}
            <div className={styles.titleRow}>
              <Typography variant="h3" className={styles.name}>
                {option.name}
              </Typography>
              {onPick && <ChevronRight className={`dir-flip ${styles.pickChevron}`} size={18} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />}
            </div>
            {option.tagline && (
              <Typography variant="p2" color="secondary" className={styles.tagline}>
                {option.tagline}
              </Typography>
            )}
            {/* The outcome this band is a bet on, and the thing the two panels differ on before they
                differ on anyone hired. It KEEPS a visible label where the team goes without one: the
                tagline sits directly above it, so two prose lines separated only by ink colour read as
                one paragraph, and the label is what says this line is a commitment rather than more
                description. */}
            {option.goal && (
              <div className={styles.section}>
                <Typography variant="h4" className={styles.sectionLabel}>
                  {t('band.offerCard.goal')}
                </Typography>
                <Typography variant="p2">{option.goal}</Typography>
              </div>
            )}
            {/* No visible label over the team: the panel is already titled with the band's name and the
                rows under it are self-evidently people, so the word was spent on nothing. The APPS keep
                theirs below, because a row of chips is the one part a first-time reader could misread.
                Both phones draw the same pair; the label still reaches a screen reader as the group's
                own name. */}
            <div className={styles.section} role="group" aria-label={t('band.offerCard.team')}>
              <BandAgentRows agents={option.agents} />
            </div>
            {option.apps.length > 0 && (
              <div className={styles.section}>
                <Typography variant="h4" className={styles.sectionLabel}>
                  {t('band.offerCard.apps')}
                </Typography>
                {/* NAMED chips, wrapping. A bare plate cluster asked the owner to weigh two bands on apps
                    it never named — Google Calendar and Wix Stores were anonymous grey squares. */}
                <BandAppChips apps={option.apps} ariaLabel={t('band.offerCard.apps')} />
              </div>
            )}
          </Card>
        ))}
      </div>
    </Card>
  );
}
